import React, {
  useState, useEffect, useRef,
} from 'react'
import { Map, List, fromJS } from 'immutable'
import _get from 'lodash/get'
import _size from 'lodash/size'
import _trim from 'lodash/trim'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import FormsyForm from 'formsy-react'
import { useOnScreen } from '../../services/pm-screen/util/index'
import { getBoundActions } from '../../actions/index'
import { EN } from '../../services/languages/constants'
import { isDefault as isDefaultLanguage } from '../../services/languages/index'
import { getUserLanguagePrimary } from '../../services/user/index'
import { selectFirstName } from '../../services/user/selectors'
import {
  AI_MODULE_VIEWED_GA4_EVENT,
  AI_MESSAGE_SENT_GA4_EVENT,
  AI_CONVERSATION_STARTED_GA4_EVENT,
  UI_LOCATION,
  UI_ELEMENT,
} from '../../services/event-tracking/index'
import { selectDefaultOraclePersonalityId } from '../../services/feature-tracking/selectors'
import { historyRedirect } from '../../services/navigation/index'
import { URL_AI_CHAT } from '../../services/url/constants'
import {
  PERSONALITY_ARCHITECT_ID,
  PERSONALITY_SAGE_ID,
} from '../../services/oracle/index'
import { Textarea } from '../FormInput.v2/index'
import Link, { TARGET_BLANK } from '../Link/index'
import Button, { TYPES, SIZES } from '../Button/index'
import ArrowUp from '../Icon/icons/ArrowUp/index'
import VoiceMode from '../Icon/icons/VoiceMode/index'
import { ICON_COLORS } from '../Icon/index'
import { usePersonalizedSeedQuestionListQuery } from '../OraclePage/queries.generated'
import { GuideLogo, OraclePillButton } from '../OracleCommons/index'

const formName = 'userPrompt'

// Keyboard key constants
const KEYBOARD_KEYS = {
  ENTER: 'Enter',
}

/**
 * Get the persona description text based on selected personality and user's first name
 * @param {string} personalityId - The selected personality ID
 * @param {string} firstName - The user's first name
 * @param {Object} staticText - The static text map from Redux
 * @returns {string} - The description text
 */
function getPersonaDescription (personalityId, firstName, staticText) {
  const name = firstName || staticText.get('friendFallback', 'Friend')

  if (personalityId === PERSONALITY_SAGE_ID) {
    return staticText.get('sageDescription', '').replace('{name}', name)
  }

  if (personalityId === PERSONALITY_ARCHITECT_ID) {
    return staticText.get('architectDescription', '').replace('{name}', name)
  }

  return ''
}

/**
 * Get the event data for the promotion
 * @param {Object} data - The promotion data
 * @param {Object} props - The props
 * @returns {Object} - The event data
 */
function getEventData (data, props) {
  const {
    promotionContentId,
    promotionContentType,
    rowPlacement,
  } = props

  const title = data.get('title')
  const parentSeriesId = data.get('seriesId')
  const parentSeriesTitle = data.get('seriesTitle')
  const hasParentSeries = !!parentSeriesId && parentSeriesId > 0

  return {
    ui_location: UI_LOCATION.MEMBER_HOME,
    ui_element: UI_ELEMENT.TEXT_FIELD,
    promo_name: title,
    content_id: promotionContentId,
    content_type: promotionContentType,
    content_name: title,
    row_placement: rowPlacement,
    row_name: title,
    tile_number: 0,
    click_attribution: UI_LOCATION.MEMBER_HOME,
    ...(hasParentSeries && {
      parent_series_id: parentSeriesId,
      parent_series_title: parentSeriesTitle,
    }),
  }
}

export const DISPLAY_NAME = 'PromotionGaiaOracle'

function PromotionGaiaOracle (props) {
  const {
    data = Map(),
    loading,
    visibilityThreshold = 0.80,
    sectionId,
    staticText,
    history,
    auth,
    language,
    defaultOraclePersonalityId,
    setAiGa4Event,
    oracleVoiceAccess,
    firstName,
  } = props
  const elementRef = useRef(null)
  const [elementRefReady, setElementRefReady] = useState(false)
  const [isVisibleGaEventSent, setIsVisibleGaEventSent] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [promptValue, setPromptValue] = useState('')
  const [selectedPersonality, setSelectedPersonality] = useState(defaultOraclePersonalityId)
  const isEnglishUser = isDefaultLanguage(language)

  // Fetch personalized seed questions from GraphQL
  const {
    data: seedData,
  } = usePersonalizedSeedQuestionListQuery({
    variables: {
      limit: 3,
      language,
    },
    context: {
      jwt: auth.get('jwt'),
    },
  })

  // Get seed questions from GraphQL data and convert to prompts format
  const seedQuestions = seedData?.userInfoCurrent?.userInfo?.seedQuestions || []
  const prompts = List(seedQuestions.map((seed) => Map({
    id: seed.id,
    label: seed.question,
    language: seed.language,
  })))
  const PERSONALITY_OPTIONS = fromJS([
    // Order: Sage first (ID 2), then Architect+ (ID 1) to match Figma design
    { value: PERSONALITY_SAGE_ID, name: staticText.get('personalitySage', 'Gaia Sage') },
    { value: PERSONALITY_ARCHITECT_ID, name: staticText.get('personalityArchitect', 'Architect+') },
  ])
  const setElementRef = (el) => {
    elementRef.current = el
    setElementRefReady(!!el)
  }

  // useOnScreen hook now handles useEffect internally and checks for element existence
  useOnScreen(elementRef, visibilityThreshold, setIsVisible, elementRefReady)

  useEffect(() => {
    // Only send GA data once
    if (isVisible && !isVisibleGaEventSent) {
      const { setEventDefault } = props

      // Send GA4 event
      const eventData = getEventData(data, props)
      setEventDefault(AI_MODULE_VIEWED_GA4_EVENT.mergeDeep(eventData))
      setIsVisibleGaEventSent(true)
    }
  }, [isVisible])

  // Set default personality - force Sage for non-English users
  useEffect(() => {
    const personality = isEnglishUser ? defaultOraclePersonalityId : PERSONALITY_SAGE_ID
    setSelectedPersonality(personality)
  }, [defaultOraclePersonalityId, isEnglishUser])

  const onChangePersonality = (option) => {
    if (_get(option, 'value') === '') {
      return
    }

    setSelectedPersonality(_get(option, 'value'))
  }

  const handleSubmit = (formData) => {
    const userPrompt = formData[formName]

    if (!userPrompt || _size(userPrompt) === 0) {
      return
    }

    // AI GA4 events for text entered
    setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: UI_ELEMENT.TEXT_FIELD,
      input_method: 'text entered',
      ai_persona: selectedPersonality,
    }))
    setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
      ui_element: UI_ELEMENT.TEXT_FIELD,
      ai_persona: selectedPersonality,
    }))

    historyRedirect({
      history,
      url: URL_AI_CHAT,
      query: { prompt: userPrompt, oraclePersonalityId: selectedPersonality },
      auth,
      language,
    })
  }

  // AI GA4 events for prompt clicked
  const handleSendPromptGa4Event = () => {
    setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: UI_ELEMENT.PROMPT,
      input_method: 'prompt',
      ai_persona: selectedPersonality,
    }))
    setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
      ui_element: UI_ELEMENT.PROMPT,
      ai_persona: selectedPersonality,
    }))
  }

  // AI GA4 events for voice mode clicked
  const handleSendVoiceModeGa4Event = () => {
    setAiGa4Event(AI_CONVERSATION_STARTED_GA4_EVENT.mergeDeep({
      ui_element: UI_ELEMENT.VOICE_MODE,
      ai_persona: selectedPersonality,
    }))
  }

  const onChange = (value) => {
    setPromptValue(value)
  }

  const handleKeyDown = (event) => {
    // If Enter is pressed without Shift, submit the form
    if (event.key === KEYBOARD_KEYS.ENTER && !event.shiftKey) {
      event.preventDefault()

      // Only submit if there's content
      if (promptValue && _size(_trim(promptValue)) > 0) {
        handleSubmit({ [formName]: promptValue })
      }
    }
    // If Shift+Enter is pressed, allow default behavior (new line)
    // No need to handle this case as it's the default behavior
  }

  if (loading || !Map.isMap(data)) {
    return null
  }

  return (
    <div
      ref={setElementRef} data-page-manager-section-id={sectionId}
      className="promotion-gaia-oracle"
    >
      <div className="promotion-gaia-oracle__wrapper">
        <FormsyForm onValidSubmit={handleSubmit} className="promotion-gaia-oracle__form">
          <div className="promotion-gaia-oracle__header">
            <Link
              className="promotion-gaia-oracle__logo"
              to={URL_AI_CHAT}
              query={{ oraclePersonalityId: selectedPersonality }}
            >
              <GuideLogo className="promotion-gaia-oracle__wordmark" />
            </Link>
            {isEnglishUser && (
              <div className="promotion-gaia-oracle__persona-pills">
                {PERSONALITY_OPTIONS.toJS().map((persona) => {
                  const isSelected = persona.value === selectedPersonality
                  return (
                    <OraclePillButton
                      key={persona.value}
                      className="oracle-pill-button--toggle"
                      selected={isSelected}
                      onClick={() => onChangePersonality(persona)}
                      ariaPressed={isSelected}
                      ariaLabel={staticText.get('selectPersonality', 'Select {name}').replace('{name}', persona.name)}
                    >
                      {persona.name}
                    </OraclePillButton>
                  )
                })}
              </div>
            )}
          </div>
          <p className="promotion-gaia-oracle__description">
            {getPersonaDescription(selectedPersonality, firstName, staticText)}
          </p>
          <div className="promotion-gaia-oracle__form-content">
            <Textarea
              className="promotion-gaia-oracle__input"
              name={formName}
              label=""
              hideLabel
              placeholder={staticText.get('placeholder', '')}
              value={promptValue}
              maxLength={1001}
              showCharCount
              validations="maxLength:1000"
              onChange={onChange}
              onKeyDown={handleKeyDown}
            />
            <div className="promotion-gaia-oracle__form-footer">
              <div className="promotion-gaia-oracle__prompts">
                {prompts.size > 0 && prompts.map((prompt, index) => {
                  const promptKey = prompt.get('id') || index
                  const promptLabel = prompt.get('label')
                  return (
                    <Link
                      key={promptKey || index}
                      className="promotion-gaia-oracle__prompt"
                      to={URL_AI_CHAT}
                      query={{ prompt: promptLabel, oraclePersonalityId: selectedPersonality }}
                      auth={auth}
                      onClick={handleSendPromptGa4Event}
                    >
                      {promptLabel}
                    </Link>
                  )
                })}
              </div>
              <Button
                type={TYPES.DEFAULT}
                size={SIZES.XSMALL}
                onClick={!promptValue ? handleSendVoiceModeGa4Event : null}
                submit={!!promptValue}
                className="promotion-gaia-oracle__submit-button"
                url={!promptValue ? URL_AI_CHAT : undefined}
                query={{
                  oraclePersonalityId: selectedPersonality,
                  ...(!promptValue && oracleVoiceAccess ? { voiceMode: true } : {}),
                }}
              >
                {
                  !promptValue && oracleVoiceAccess
                    ? <VoiceMode size={22} color={ICON_COLORS.WHITE} />
                    : <ArrowUp size={22} color={ICON_COLORS.WHITE} />
                }
              </Button>
            </div>
            {isEnglishUser && selectedPersonality === PERSONALITY_ARCHITECT_ID && (
              <p className="promotion-gaia-oracle__about-architect-plus">
                <Link
                  className="promotion-gaia-oracle__about-architect-plus-link"
                  to={'https://www.gaia.com/lp/getting-to-know-the-architect'}
                  target={TARGET_BLANK}
                  directLink
                >
                  {`${staticText.get('learnAboutArchitectPlus')} `}
                  &rarr;
                </Link>
              </p>
            )}
          </div>
        </FormsyForm>
      </div>
    </div>
  )
}

export default compose(
  connectRedux(
    (state, props) => {
      const {
        auth,
        user,
        pmSection,
        staticText,
        oracle,
      } = state
      const { sectionId } = props
      // Tabs are in the union type data, so access data.data.tabs
      const promotionContentId = pmSection.getIn([sectionId, 'data', 'data', 'tabs', '0', 'tabContent', '0', 'contentId'])
      const promotionContentType = pmSection.getIn([sectionId, 'data', 'data', 'tabs', '0', 'tabContent', '0', 'contentType'])
      // Get recommId for tracking purposes
      const recommId = pmSection.getIn([sectionId, 'data', 'data', 'recommId'])
      const language = getUserLanguagePrimary(user) || EN
      // displayMode is in the union type data, so access data.data.displayMode
      const displayMode = pmSection.getIn([sectionId, 'data', 'data', 'displayMode'], '')
      const oracleVoiceAccess = oracle.getIn(['access', 'aiSpeech'], false)
      const firstName = selectFirstName(state)

      return {
        language,
        auth,
        promotionContentId,
        promotionContentType,
        recommId,
        staticText: staticText.getIn(['data', 'promotionGaiaOracle', 'data'], Map()),
        defaultOraclePersonalityId: selectDefaultOraclePersonalityId(state),
        displayMode,
        oracleVoiceAccess,
        firstName,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setUpstreamContext: actions.upstreamContext.setUpstreamContext,
        setEventDefault: actions.eventTracking.setEventDefault,
        setAiGa4Event: actions.eventTracking.setAiGa4Event,
      }
    },
  ),
)(PromotionGaiaOracle)
